import { Container, Graphics, Sprite, TextStyle, Text, Spritesheet } from 'pixi.js';
import { COLOR_NAVY, COLOR_WHITE, FONT_FAMILY, PLAYER_RADIUS, PLAYER_SIZE } from '../constants';
import { IconButton } from './button';
import { Player } from './player';
import { DropShadowFilter } from 'pixi-filters';

export default class PlayerUI extends Container {
  private player: Player;
  public set userName(value: string) {
    const nameTagGraphics = this.getChildByName('name-tag-graphics') as Graphics;
    this.nameTag = value;
    if (!value || value.length === 0) {
      nameTagGraphics.visible = false;
      return;
    }
    this.drawBackground(this._nameTag?.width ?? 0);
    this.position.set(0, PLAYER_SIZE / 2);
  }

  private _nameTag: Text;
  public get nameTag(): string {
    return this._nameTag?.text ?? '???';
  }
  public set nameTag(value: string) {
    if (!this._nameTag) {
      return;
    }
    this._nameTag.text = value;
  }

  private userInfoIcon: Text;

  private _micOffIndicator: Container | undefined;
  public get micOffIndicator() {
    return this._micOffIndicator;
  }
  public set micOffIndicator(value) {
    this._micOffIndicator = value;
  }

  public updatePlayer(player: Player) {
    this.player = player;
  }

  public updateMicIndicatorIcon() {
    if (!this.player.isPrimaryPlayer) {
      this.audioButton.icon = this.player.remoteMicEnabled ? 'volume_up' : 'volume_off';
    } else {
      this.audioButton.icon = this.player.micEnabled ? 'mic' : 'mic_off';
    }
  }

  public audioButton: IconButton;
  public videoButton: IconButton;
  private chatButton: IconButton;
  private chatX: number;

  public constructor(player: Player, pixiFilters: boolean, sprites: Spritesheet) {
    super();

    this.y = 100;
    this.name = 'name-tag-container';
    this.zIndex = 1;
    applyDropShadowFilter(this, pixiFilters);

    this.player = player;

    const baseNameTagOffsetX = -1 * (PLAYER_SIZE / 2);
    this.position.set(baseNameTagOffsetX, PLAYER_SIZE / 2);

    const nameTagButtonOffsetRightX = PLAYER_RADIUS;
    const nameTagButtonOffsetLeftX = PLAYER_RADIUS * -1;

    this.chatX = nameTagButtonOffsetRightX;
    const nameTagOffsetY = -50;

    const textStyle = new TextStyle({
      fill: COLOR_NAVY,
      fontFamily: FONT_FAMILY,
      fontSize: 19,
      padding: 4,
    });

    this._nameTag = new Text('???', textStyle);
    this._nameTag.anchor.set(0.5, 0.6);
    this._nameTag.name = 'name-tag';
    this._nameTag.resolution = 2;
    this._nameTag.visible = false;

    this.userInfoIcon = new Text(
      'info',
      new TextStyle({
        fontFamily: 'Material Icons',
        fontSize: 22,
        fill: COLOR_NAVY,
        padding: 4,
        lineHeight: 2,
      }),
    );
    this.userInfoIcon.anchor.set(0.5, 0.4);
    this.userInfoIcon.name = 'user-info-icon';
    this.userInfoIcon.visible = false;

    this.micOffIndicator = this.createMicOffIndicator(pixiFilters, sprites);

    const nameTagGraphicsPadding = 8;
    const nameTagGraphics = new Graphics();
    nameTagGraphics.name = 'name-tag-graphics';
    nameTagGraphics.visible = false;

    this._nameTag.x = -nameTagGraphicsPadding;
    this._nameTag.anchor.set(0.5, 0.5);

    if (!player.isOnboardingPlayer) {
      this.on('pointertap', () => {
        player.toggleBadgeOpen(true);
      });
    } else {
      this.on('pointertap', () => {
        this.player.toggleOnboardingMockBadge();
      });
    }
    this.eventMode = 'static';
    this.addChildAt(nameTagGraphics, 0);
    this.addChildAt(this._nameTag, 1);
    this.addChildAt(this.userInfoIcon, 2);

    // ** AUDIO BUTTONS ** //
    const micX = nameTagButtonOffsetLeftX;
    const icon = player.isPrimaryPlayer ? 'mic' : 'volume_up';

    this.audioButton = new IconButton(
      {
        icon: icon,
        x: micX,
        y: nameTagOffsetY,
        onTap: () => {
          if (player.isPrimaryPlayer) {
            player.enableMic(!player.micEnabled);
          } else {
            player.enableRemoteMic(!player.remoteMicEnabled);
          }
        },
      },
      sprites,
    );
    applyDropShadowFilter(this.audioButton, pixiFilters);

    const videoX = nameTagButtonOffsetRightX;
    // ** VIDEO BUTTON ** //
    this.videoButton = new IconButton(
      {
        icon: 'videocam',
        x: videoX,
        y: nameTagOffsetY,
        onTap: () => {
          const enabled = !player.videoEnabled;
          player.enableVideo(enabled);
        },
      },
      sprites,
    );
    applyDropShadowFilter(this.videoButton, pixiFilters);

    // ** CHAT BUTTON ** //
    this.chatButton = new IconButton(
      {
        icon: 'textsms',
        x: this.chatX,
        y: nameTagOffsetY,
        onTap: () => {
          const { userId } = player.user;
          if (!userId) {
            return;
          }
          player.chatService.triggerPrivateMessage(userId);
        },
      },
      sprites,
    );
    applyDropShadowFilter(this.chatButton, pixiFilters);
    this.chatButton.position.set(this.chatX, nameTagOffsetY);
    this.onZoom(1);
    this.addChild(this.audioButton, this.chatButton, this.videoButton, this.micOffIndicator);
  }

  public onZoom(zoom: number) {
    // TODO: animate this
    // ease.add(this.audioButton, { x: PLAYER_RADIUS * -1 * zoom }, { duration: 100 });
    // ease.add(this.micOffIndicator, { x: PLAYER_RADIUS * -1 * zoom }, { duration: 100 });
    this.audioButton.x = PLAYER_RADIUS * -1 * zoom;
    this.videoButton.x = PLAYER_RADIUS * zoom;
    this.chatButton.x = this.chatX * zoom;
    if (this.micOffIndicator) {
      this.micOffIndicator.x = PLAYER_RADIUS * -1 * zoom;
    }
  }

  public show(zoom: number) {
    this.player.zIndex++;
    this.cursor = 'pointer';
    this.onZoom(zoom);
    this.zIndex++;
    this._nameTag.visible = true;
    const nameTagGraphics = this.getChildByName('name-tag-graphics') as Graphics;
    nameTagGraphics.visible = true;
    this.userInfoIcon.x = nameTagGraphics.width / 2 - 16;
    this.userInfoIcon.visible = true;
    this.zIndex++;

    this.updateMicIndicatorIcon();

    if (!this.player.isUserPresenting && !this.player.isOnboardingPlayer) {
      if (!this.player.isPrimaryPlayer) {
        // Show button only if user has own mic enabled
        if (this.player.micEnabled) {
          this.audioButton?.show();
        } else {
          this.audioButton?.hide();
        }
      }
    }
    if (!this.player.isOnboardingPlayer) {
      this.chatButton?.show();
    }
  }

  public hide() {
    this.player.zIndex--;
    this._nameTag.visible = false;
    this.userInfoIcon.visible = false;
    const nameTagGraphics = this.getChildByName('name-tag-graphics') as Graphics;
    nameTagGraphics.visible = false;
    this.zIndex--;
    this.audioButton.hide();
    this.videoButton.hide();
    this.chatButton.hide();
  }

  private drawBackground(nameLength: number): void {
    const nameTagGraphicsPadding = 8;
    const nameTagGraphicsHeight = 33;

    const nameTagGraphicsWidth = nameLength + 6.5 * nameTagGraphicsPadding;
    const graphics = new Graphics()
      .beginFill(COLOR_WHITE)
      .drawRoundedRect(0, 0, nameTagGraphicsWidth, nameTagGraphicsHeight, 14)
      .endFill();
    graphics.name = 'name-tag-graphics';
    graphics.position.set(-nameTagGraphicsWidth / 2, -nameTagGraphicsHeight / 2);
    graphics.visible = false;

    const nameTagGraphics = this.getChildByName('name-tag-graphics');
    if (nameTagGraphics) {
      this.removeChild(nameTagGraphics);
    }
    this.addChildAt(graphics, 0);
  }

  // TODO DRY this up with IconButton class
  private createMicOffIndicator(pixiFilters: boolean, sprites: Spritesheet): Container {
    const container = new Container();
    container.name = 'mic-off-indicator';
    container.y = -50;

    const sprite = new Sprite(sprites.textures['hover-icon-background.svg']);
    sprite.name = 'mic-off-indicator-sprite';
    sprite.anchor.set(0.5, 0.5);

    const icon = this.player.isPrimaryPlayer ? 'mic_off' : 'volume_off';
    const indicator = new Text(
      icon,
      new TextStyle({
        fontFamily: 'Material Icons',
        fontSize: 20,
        fill: COLOR_NAVY,
        lineHeight: 20,
        padding: 0,
      }),
    );

    indicator.anchor.set(0.5, 0.5);
    indicator.pivot.set(1, -3);

    container.addChild(sprite, indicator);
    container.visible = false;
    applyDropShadowFilter(container, pixiFilters);

    container.eventMode = 'static';
    container.on('pointertap', ($event) => {
      //Avoid opening smart badge
      $event.stopPropagation();
      $event.preventDefault();
      // Allow remote muting only if user has own mic enabled
      if (!this.player.isPrimaryPlayer && this.player.micEnabled) {
        const enabled = !this.player.remoteMicEnabled;
        this.player.enableRemoteMic(enabled);
      }
    });

    return container;
  }

  public destroy(): void {
    this.removeChildren();
  }
}

export function applyDropShadowFilter(container: Container, pixiFilters: boolean): void {
  if (!pixiFilters) {
    return;
  }
  const dropShadowFilterInstance = new DropShadowFilter();
  dropShadowFilterInstance.offset.y = 10;
  container.filters = [dropShadowFilterInstance];
}
